import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import LevelChip from './LevelChip';
import { selectTreeIndex } from '../../../redux/context/selectors';
import { ancestorsOf } from '../../../redux/context/treeUtils';
import { 
  useCurrentNodeId, 
  useOpenNode 
} from '../../../redux/context/navigation';
import type { TreeNode } from '../../../service/tree.service';

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
const SEARCH_LABEL = 'Search organisations, entities and workspaces';

/** A visible pointer to the switcher's Ctrl+K shortcut; clicking it opens the switcher. */
const SearchHint: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title={`${SEARCH_LABEL} (${isMac ? '⌘K' : 'Ctrl+K'})`}>
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: 34,
        lineHeight: '20px',
        gap: 8,
        padding: '0 10px',
        fontSize: 13,
        color: '#6b7280',
        background: 'transparent',
        border: '1px solid #d9d9d9',
        borderRadius: 6,
        cursor: 'pointer'
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        <SearchOutlined />
        {SEARCH_LABEL}
      </span>
      <kbd
        style={{
          fontFamily: 'inherit',
          fontSize: 11,
          lineHeight: '16px',
          padding: '0 4px',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          flexShrink: 0
        }}
      >{isMac ? '⌘K' : 'Ctrl K'}</kbd>
    </button>
  </Tooltip>
);

interface ContextBreadcrumbProps {
  onOpenSwitcher?: () => void;
}

/** Where the user is, for information only; moving is done with the search or the console cards. */
const ContextBreadcrumb: React.FC<ContextBreadcrumbProps> = ({ onOpenSwitcher }) => {
  const index: any = useSelector(selectTreeIndex);
  const currentId = useCurrentNodeId();
  const open = useOpenNode();

  const current: TreeNode | undefined = currentId != null ? index?.[currentId] : undefined;
  if (!current) return null;

  const segments = [...ancestorsOf(index, current.id), current].filter(
    (n, i, arr) => arr.findIndex((x) => x.id === n.id) === i
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr minmax(260px, 480px) 1fr',
        alignItems: 'center',
        width: '100%',
        gap: 12
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        {segments.map((seg, i) => {
          const label = (
            <>
              <LevelChip level={seg.kind} compact />
              <span
                title={seg.name}
                style={{
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {seg.name}
              </span>
            </>
          );
          const isCurrent = i === segments.length - 1;
          return (
            <React.Fragment key={seg.id}>
              {i > 0 && <RightOutlined
                style={{ fontSize: 10, opacity: 0.5, flexShrink: 0 }} /
              >}
              {isCurrent ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 0
                  }}
                >
                  {label}
                </span>
              ) : (
                <button
                  type="button"
                  className="hover:underline"
                  onClick={() => open(seg)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 0,
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div>{onOpenSwitcher && <SearchHint onClick={onOpenSwitcher} />}</div>
      <div />
    </div>
  );
};

export default ContextBreadcrumb;
